// types.ts
// Public types for AppShell.

import type { ReactNode } from 'react';
import type { MenuProps } from 'antd';
import type { NavItem, NavSection } from './nav-hierarchy/types';
import type { PageContainerProps } from '../PageContainer';

export type { NavItem, NavSection };

export interface AppShellProps {
  /** Text shown next to the BlueRock wordmark in the sider brand row.
   *  Defaults to "Console". Pass `''` to hide it entirely (wordmark only,
   *  no trailing gap). The wordmark + icon themselves are still not a
   *  slot — only this trailing label is configurable. */
  brandLabel?: string;

  /* ── LOCKED canonical slots ──
   * These render the canonical WorkspaceSwitcher / UserMenu helpers from the
   * provided props. They are NOT ReactNode slots — consumers cannot substitute
   * custom content. The intent is hard visual consistency across every
   * BlueRock Console app for the identity-bearing surfaces (workspace picker,
   * user/account block). Omit the prop to hide the slot entirely (e.g.,
   * single-workspace apps omit `workspace`). */

  /** Workspace picker rendered below the brand. Omit to hide the picker
   *  (e.g., single-workspace apps). The `collapsed` field is managed by
   *  AppShell — pass everything else. */
  workspace?: Omit<WorkspaceSwitcherProps, 'collapsed'>;
  /** User/account block at the bottom of the sider. Omit to hide it. The
   *  `collapsed` field is managed by AppShell. */
  user?: Omit<UserMenuProps, 'collapsed'>;

  /* ── LOCKED chrome slots ──
   * Same philosophy as workspace/user above — typed props, canonical
   * rendering, no ReactNode escape hatches. Page-specific actions belong in
   * page content, not in chrome. Additional canonical primitives
   * (notifications, search) will be added here as new typed slots when the
   * design system commits to them — never as open ReactNode props. */

  /** Theme toggle. Omit to hide (e.g., apps that don't support light/dark
   *  switching). Renders inside the UserMenu popover (requires `user` to
   *  also be set) so the control stays reachable. There is no header row —
   *  see the module doc comment — so this is the only place it can live. */
  themeToggle?: ThemeToggleProps;

  /* ── Content ── */
  children: ReactNode;
  /** Page-content wrapping. AppShell wraps `children` in a `PageContainer`
   *  by default (canonical 16/24 page inset) so every consuming app gets the
   *  same page chrome without remembering to import + wrap. Two overrides:
   *  - `false` — render `children` raw, no wrapper. For full-bleed pages
   *    (edge-to-edge dashboards, maps, data tables).
   *  - `{ maxWidth: 720, ... }` — pass props through to the wrapping
   *    `PageContainer` (e.g., narrow reading column). `children` is supplied
   *    by AppShell and cannot be overridden here. */
  pageContainer?: false | Omit<PageContainerProps, 'children'>;

  /* ── Nav (consumer-owned) ──
   * AppShell renders whatever nav tree you pass. Role / feature-flag /
   * permission gating is policy and lives in the consuming app — filter
   * your nav upstream and pass the visible-to-this-user result here. */
  /** Sectioned main nav. Rendered in the middle of the sider, grouped under
   *  optional section headings. Required. */
  nav: readonly NavSection[];
  /** Flat bottom-pinned nav, rendered just above the user menu without a
   *  section heading (e.g., "Developers", "Help"). Omit to render nothing. */
  bottomNav?: readonly NavItem[];

  /* ── Collapse state (unchanged from V1) ── */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  collapsePersistenceKey?: string;

  /* ── Selection state ── */
  /** Current URL path from the consumer's router. AppShell derives selectedKeys/openKeys via IA path matching. */
  currentPath?: string;
  /** Explicit override of derived selection (controlled mode). */
  selectedKeys?: string[];
  /** Fired when a nav item is clicked. */
  onSelect?: (key: string, item: NavItem) => void;
  openKeys?: string[];
  onOpenChange?: (keys: string[]) => void;

  /* ── Layout overrides (unchanged) ── */
  maxContentWidth?: number;
  siderWidth?: number;
  siderCollapsedWidth?: number;
}

export interface ThemeToggleProps {
  mode: 'light' | 'dark';
  onChange: (next: 'light' | 'dark') => void;
}

export interface WorkspaceSwitcherWorkspace {
  id: string;
  name: string;
  /** Plan tier (e.g., "Team", "Dev"). Shown as part of the dropdown item
   *  subtitle when set. */
  plan?: string;
  /** User's role in this workspace (e.g., "admin", "owner"). Shown as part
   *  of the dropdown item subtitle when set. */
  role?: string;
}

export interface WorkspaceSwitcherProps {
  workspaces: readonly WorkspaceSwitcherWorkspace[];
  currentId?: string;
  onChange: (id: string) => void;
  /** Whether the sider is collapsed — drives compact rendering. AppShell wires this. */
  collapsed?: boolean;
}

export interface UserMenuUser {
  firstName?: string;
  lastName?: string;
  email: string;
  avatarUrl?: string;
}

export interface UserMenuUsage {
  plan: string;
  used: number;
  limit: number;
  /** Optional unit label appended after the X/Y count (e.g., "scans",
   *  "queries", "users"). When omitted, the count renders without a label. */
  unit?: string;
}

export interface UserMenuRoleBadge {
  label: string;
  /** AntD preset palette name (e.g. 'green', 'blue') or a hex value,
   *  passed straight through to antd's <Tag color>. corenet does not
   *  own role-to-color mapping — the consumer computes this. */
  color?: string;
}

export interface UserMenuMockDataToggle {
  enabled: boolean;
  onChange: (next: boolean) => void;
  /** Defaults to "Mock data" if omitted. */
  label?: string;
}

export interface UserMenuProps {
  user: UserMenuUser;
  usage?: UserMenuUsage;
  /** Items rendered in the popover dropdown (Settings, Billing, etc.). Consumer-controlled. */
  menuItems?: MenuProps['items'];
  onSignOut: () => void;
  /** Whether the sider is collapsed. AppShell wires this. */
  collapsed?: boolean;
  /** Role/permission badge shown next to the identity block in the
   *  popover. Omit to hide. */
  roleBadge?: UserMenuRoleBadge;
  /** Labeled Switch row in the popover for toggling mock data. Omit
   *  to hide. */
  mockDataToggle?: UserMenuMockDataToggle;
  /** Light/dark toggle row in the popover. AppShell sets this itself when
   *  `header={false}` and a top-level `themeToggle` prop was supplied — not
   *  meant to be set directly by consumers. Omit to hide. */
  themeToggle?: ThemeToggleProps;
}

export const APP_SHELL_DEFAULTS = {
  maxContentWidth: 2200,
  siderWidth: 240,
  siderCollapsedWidth: 72,
  headerHeight: 50,
  collapsePersistenceKey: 'bluerock:appshell:collapsed',
  brandLabel: 'Console',
} as const;
